/**
 * Rendered by `RequireRole` when the caller's local mirror lacks the
 * required role. Surfaces the denial reason instead of silently
 * redirecting — operators reported that a bounce to /me looked like
 * a broken nav link rather than an authorisation outcome.
 *
 * UI-only. The backend has already denied any direct API call;
 * this is the client's reflection of that decision.
 */

import { Alert, Anchor, Badge, Container, Group, Stack, Text } from "@mantine/core";
import { Link } from "react-router-dom";

interface Props {
  requiredRole: string;
  callerRoles: readonly string[];
}

export function ForbiddenPage({ requiredRole, callerRoles }: Props) {
  return (
    <Container size="sm" py="xl">
      <Alert variant="light" color="red" title="Access denied" role="alert">
        <Stack gap="sm">
          <Text size="sm" span>
            This page requires the{" "}
            <Badge color="red" variant="light" component="span">
              {requiredRole}
            </Badge>{" "}
            role. Your account does not currently have it.
          </Text>
          <Group gap="xs">
            <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
              Your roles
            </Text>
            {callerRoles.length === 0 ? (
              <Text size="sm" c="dimmed">
                (none)
              </Text>
            ) : (
              callerRoles.map((r) => (
                <Badge key={r} color="gray" variant="light">
                  {r}
                </Badge>
              ))
            )}
          </Group>
          <Text size="sm">
            If you believe this is wrong, contact a tenant administrator. Otherwise{" "}
            <Anchor component={Link} to="/me">
              return to your profile
            </Anchor>
            .
          </Text>
        </Stack>
      </Alert>
    </Container>
  );
}

